<template>
  <router-view v-if="isMobile && $route.name !== 'user'"/>
  <profile-main-box v-else
                    :profile="getUserProfile"
                    :actions-list="actionsList"
                    :default-action="profileStatus"
                    :has-edit-profile="user && $route.name !== 'editUser' && $u.checkPermissions('user.update')"
                    :content-class="getContentClass"
                    @editProfile="editPromptStatus = true"
                    @userTaskList="handleChangeProfileStatus('userTaskList')"
                    @userTicketsList="handleChangeProfileStatus('userTicketsList')">
    <router-view @edited="handleReloadProfile" @inserted="handleReloadProfile" @insertedNew="getUser"/>

    <custom-modal :active="editPromptStatus"
                  :fixed-height="false"
                  @close="editPromptStatus = false">
      <template #header>
        <div class="prompt-header p-3 w-full" :class="[!$vs.rtl ? 'rtl-only' : '']">
          <vs-row>
            <vs-col class="w-1/5 useral-font-weight-medium text-success cursor-pointer">
              <div @click="$u.click('EditUserBTN')">
                <custom-icon icon="save-icon" color="success"/>
              </div>
            </vs-col>

            <vs-spacer/>

            <vs-col class="w-1/2 text-center useral-font-weight-bold text-md">
              {{ $t('users.edit.title') }}
            </vs-col>

            <vs-spacer/>

            <vs-col class="w-1/5 text-right useral-font-weight-medium text-danger cursor-pointer flex">
              <div class="mx-2" v-if="$u.checkPermissions('user.delete')" @click="$u.click('deleteUserBTN')">
                <custom-icon color="danger"
                             icon="trash-icon"/>
              </div>
              <div @click="editPromptStatus = false">
                <custom-icon icon="circle-x-icon" color="danger"/>
              </div>
            </vs-col>
          </vs-row>
        </div>
      </template>
      <template #content>
        <div class="prompt-content">
          <template>
            <keep-alive>
              <edit-user :in-modal="true" @edited="handleUserEdited" />
            </keep-alive>
          </template>
        </div>
      </template>
    </custom-modal>
  </profile-main-box>
</template>

<script>

import {mapGetters} from 'vuex'
import {getUser} from '@/http/requests/users'
import EditUser from '@/views/users/edit/EditUser.vue'
import CustomIcon from '@/components/icon/customIcon.vue'
import CustomModal from '@/components/modal/customModal.vue'

export default {
  name: 'index',
  components: {
    CustomModal,
    CustomIcon,
    EditUser,
    ProfileMainBox: () => import('@/components/profileMainBox/profileMainBox')
  },
  metaInfo () {
    return {
      title: this.user ? `${this.$t('users.profile.dynamicTitle')} ${this.user.name.value} ${this.user.lastName.value}` : this.$t('users.profile.title')
    }
  },
  data () {
    return {
      editPromptStatus: false,
      setUserMaxDebtPromptStatus: false,
      showUserAddressesPromptStatus: false,
      showUserTransactionsPromptStatus: false,
      profileStatus: '',
      actions: {
        toolbar: [],
        leftToolbar: [
          {
            id: 'routeBackBTN',
            route: {name: 'users'},
            icon: 'chevron-left-icon',
            iconPack: 'useral'
          }
        ]
      },
      user: null,
      actionsList: [
        {
          event: 'userTaskList',
          name: 'userTaskList',
          params: {id: this.userId || this.$route.params.id},
          header: this.$t('tasks.list.title'),
          permission: 'task.show',
          i18n: '',
          icon: 'square-check-icon',
          iconPack: 'useral'
        },
        {
          event: 'userTicketsList',
          name: 'userTicketsList',
          params: {id: this.userId || this.$route.params.id},
          header: this.$t('tickets.list.title'),
          permission: 'ticket.show',
          i18n: '',
          icon: 'SUPPORT',
          iconPack: 'useral'
        }
      ]
    }
  },
  created () {
    if (!this.userId) {

      this.$nextTick(() => {
        this.$store.dispatch('updateNavbarActions', this.actions)
        this.$store.dispatch('updateContentNavbarStyle', 'sticky')
        this.$store.dispatch('updateContentNavbarClass', 'profile-page-navbar mx-auto')
      })
    }

    this.profileStatus = this.$route.name
    if (!this.isMobile && this.$route.name === 'user') {
      if (this.$u.checkPermissions('task.show')) {
        this.handleChangeProfileStatus('userTaskList')
      } else if (this.$u.checkPermissions('user.update')) {
        this.handleChangeProfileStatus('editUser')
      } else {
        this.handleChangeProfileStatus('sendMessageToUser')
      }
    }

    this.getUser()
  },
  computed: {
    ...mapGetters({
      isMobile: 'isMobile'
    }),
    getUserProfile () {
      if (!this.user) {
        return {}
      }

      const user = {
        avatar: {
          value: this.user.avatar ? this.$u.getAssetsUrl(this.user.avatar) : this.user.gender === 1 ? require('@/assets/images/man-avatar.png') : require('@/assets//images/woman-avatar.png'),
          size: 'small'
        },
        name: `${this.user.name.value} ${this.user.lastName.value}`,
        details: [],
        importantData: [
          {
            key: this.$t('users.general.labels.email'),
            value: this.user.email.value
          },
          {
            key: this.$t('users.general.labels.role'),
            value: this.user.role && this.user.role.value ? this.user.role.value : '-'
          },
          {
            key: this.$t('users.general.labels.totalTime'),
            value: this.user.totalTime
          }
        ],
        description: this.user.description
      }

      return user
    },
    getContentClass () {
      const contentClass = ['userTaskList', 'userTicketsList'].includes(this.$route.name) ? 'without-padding' : ''
      return contentClass
    }
  },
  watch: {
    '$route.name': {
      handler (val) {
        if (val === 'user') {
          this.$nextTick(() => {
            this.$store.dispatch('updateNavbarActions', this.actions)
            this.$store.dispatch('updateContentNavbarStyle', 'sticky')
            this.$store.dispatch('updateContentNavbarClass', 'mx-auto')
          })
          this.getUser()
        }
      }
    }
  },
  methods: {
    getUser () {

      getUser(this.userId || this.$route.params.id).then(res => {
        const user = res.data.data

        this.user = {
          editable: user.editable,
          id: user.id,
          avatar: user.avatar,
          gender: user.gender || 1,
          name: {
            value: user.name,
            isInvalid: ''
          },
          company: {
            value: user.company ? user.company : '',
            isInvalid: ''
          },
          role: {
            value: user.role ? user.role.name : '',
            isInvalid: ''
          },
          lastName: {
            value: user.family,
            isInvalid: ''
          },
          email: {
            value: user.email ? user.email : '',
            isInvalid: ''
          },
          phoneNumber: {
            value: user.phone_number,
            isInvalid: ''
          },
          totalTime: user.total_times || 0,
          description: user.description
        }

        if (!this.userId && !this._isBeingDestroyed && !this.isMobile && this.$route.name === 'user') {
          this.$store.dispatch('setPageTitle', `${this.$t('users.profile.dynamicTitle')} ${this.user.general.name.value} ${this.user.general.lastName.value}`)
        }

        const whatsApp_index = this.actionsList.map(e => e.route).indexOf(`https://wa.me/${user.phone_number}`)
        if (whatsApp_index === -1) {
          this.actionsList.push({
            route: `https://wa.me/${user.phone_number}`,
            routeType: 'external-link',
            target: '_blank',
            header: this.$t('users.profile.actions.whatsApp'),
            type: 'important',
            i18n: '',
            colorCode: '#20B038',
            icon: 'WHATSAPP',
            iconPack: 'useral'
          })
        }

        const tell_index = this.actionsList.map(e => e.route).indexOf(`tel:${user.phone_number}`)
        if (tell_index === -1) {
          this.actionsList.push({
            route: `tel:${user.phone_number}`,
            routeType: 'external-link',
            header: this.$t('users.profile.actions.call'),
            type: 'important',
            i18n: '',
            colorCode: '#888888',
            icon: 'phone-icon',
            iconPack: 'useral'
          })
        }

        const actionsList = JSON.parse(JSON.stringify(this.actionsList))
        this.actionsList = []
        this.$nextTick(() => {
          this.actionsList = actionsList
        })

        if (this.$u.checkPermissions('user.update')) {
          const actions = JSON.parse(JSON.stringify(this.actions))
          actions.toolbar = [
            {
              id: 'editUser',
              icon: 'edit-icon',
              iconPack: 'useral',
              color: 'warning',
              permission: 'user.update'
            }
          ]
          if (this.isMobile) {
            actions.toolbar[0].id = {name: 'editUser', params: {id: this.userId || this.$route.params.id}}
            actions.toolbar[0].type = 'link'

            if (!this._isBeingDestroyed && this.$route.name === 'user') {
              this.$nextTick(() => {
                this.$store.dispatch('updateNavbarActions', actions)
              })
            }
          }
        }
      })
    },
    handleUserEdited () {
      this.editPromptStatus = false
      this.getUser()
    },
    handleChangeProfileStatus (name) {
      if (this.$route.name !== name) {
        this.profileStatus = name
        this.$router.replace({name})
      }
    },
    handleReloadProfile () {
      if (this.$u.checkPermissions('user.show_transactions')) {
        this.handleChangeProfileStatus('userTransactions')
      } else {
        this.handleChangeProfileStatus('sendMessageToUser')
      }
      this.getUser()
    }
  }
}
</script>

<style lang="scss">
.user-profile {
  height: 100%;
}
</style>
